/**
 * BundleCard — compact bundle card for the library grid (non-active bundles).
 *
 * Design source: /tmp/arianna-design/ios-reader/project/library.jsx — BundleCard function.
 * Literal replica of the iPad design: surface border, horizontal layout with
 * small BundleCover, meta row (days-to-exam coloured), progress bar.
 *
 * Styling: Tailwind v4 DS-native utilities + clsx for conditionals.
 */

import * as React from 'react'
import { clsx } from 'clsx'
import { BundleCover } from './BundleCover.js'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BundleCardBundle {
  code: string
  title: string
  subtitle: string | null
  cat: string
  version: string
  /** null means exam has passed */
  daysToExam: number | null
  paragraphsRead: number
  paragraphsTotal: number
  /** 0–1 — drives progress bar colour (1 = green, <1 = accent) */
  coverage: number
}

export interface BundleCardProps {
  bundle: BundleCardBundle
  onClick?: () => void
}

// ---------------------------------------------------------------------------
// BundleCard
// ---------------------------------------------------------------------------

/**
 * Compact bundle card for the "Altri bundle" grid.
 * Layout: small BundleCover (52×70) + right column (eyebrow, title, subtitle,
 * meta row with exam countdown or "passato", progress bar).
 */
export function BundleCard({ bundle: b, onClick }: BundleCardProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick?.()
      }}
      className={clsx(
        'flex cursor-pointer bg-surface transition-colors duration-150',
        /* design handoff — padding 16px 18px: py-4 (16px) + px-[18px] (no DS token for 18px) */
        'py-4 px-[18px]',
        /* design handoff — border-radius 12px: between radius-sm(4) and radius-md(8) and radius-lg(14); use arbitrary */
        'rounded-[12px]',
        /* design handoff — gap 14px: between space-3(12) and space-4(16); use arbitrary */
        'gap-[14px]',
        'border border-line hover:border-line-2',
      )}
    >
      <BundleCover code={b.code} title={b.title} />

      <div className="min-w-0 flex-1">
        {/* Eyebrow: cat · version */}
        {/* design handoff — fontSize 9.5px: no DS token; arbitrary value */}
        <div className="mb-[3px] font-sans text-[9.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
          {b.cat} · v{b.version}
        </div>

        {/* Title */}
        {/* design handoff — fontSize 16px: between text-base(14) and text-md(15) and text-lg(17); use arbitrary */}
        <div className="mb-[2px] overflow-hidden text-ellipsis whitespace-nowrap font-serif text-[16px] font-semibold tracking-[-0.015em] text-ink">
          {b.title}
        </div>

        {/* Subtitle */}
        {b.subtitle != null && (
          /* subtitle fontSize 12px: text-xs is 11px, deviation 1px → round to text-xs */
          <div className="mb-2 overflow-hidden text-ellipsis whitespace-nowrap font-sans text-xs text-ink-3">
            {b.subtitle}
          </div>
        )}

        {/* Meta row: days to exam or "passato" + paragraph counter */}
        <div className="flex items-center gap-2 font-sans text-xs text-ink-3">
          {b.daysToExam !== null ? (
            <span className={clsx('font-mono', b.daysToExam <= 14 ? 'text-red' : 'text-ink-3')}>
              {b.daysToExam}g
            </span>
          ) : (
            <span className="font-mono text-green">passato</span>
          )}
          {/* separator dot — 2×2px, no DS token; size-0.5 = 2px */}
          <span className="size-0.5 shrink-0 rounded-full bg-ink-4" />
          <span className="font-mono">
            {b.paragraphsRead}/{b.paragraphsTotal}
          </span>
        </div>

        {/* Progress bar */}
        {/* design handoff — height 3px: no TW spacing token; arbitrary; marginTop 7px: arbitrary */}
        <div className="mt-[7px] h-[3px] overflow-hidden rounded-[2px] bg-surface-3">
          <div
            className={clsx('h-full', b.coverage >= 1 ? 'bg-green' : 'bg-ar-accent')}
            style={{ width: `${b.coverage * 100}%` }}
          />
        </div>
      </div>
    </div>
  )
}
